import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import Button from "components/base/Button";
import Money from "components/base/InputMoney";
import { useToast } from "providers/ToastProvider";
import { transformErrors, floatFormat } from "helpers/utils";
import { useEmpresas } from "providers/EmpresaProvider";
import { useState } from "react";
import {
    Card,
    Table,
    Modal,
    Form,
    Row,
    Col,
    Alert,
    Accordion,
    Badge,
    Spinner
} from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faEdit, faTrash, faStore } from "@fortawesome/free-solid-svg-icons";
import { produtoKeys, useProdutoInvalidation } from "hooks/useProduto";

type ProdutoPrecoEmpresa = {
    produtoId: number;
    empresaId: number;
    empresaNome?: string;
    precoCompra: number;
    valor: number;
    margem: number;
    descontoMaximo?: number;
    desconto?: number;
    precoAberto: boolean;
};

type CriarPrecoEmpresaForm = {
    empresaId: number;
    precoCompra: number;
    valor: number;
    margem: number;
    descontoMaximo?: number;
    desconto?: number;
    precoAberto: boolean;
};

type Props = {
    produtoId: number;
};

const ProdutoEmpresaPrecosEspecificosTable = ({ produtoId }: Props) => {
    const [showModal, setShowModal] = useState(false);
    const [editingPreco, setEditingPreco] = useState<ProdutoPrecoEmpresa | null>(null);
    const [generalError, setGeneralError] = useState<string | null>(null);
    const [precoComDesconto, setPrecoComDesconto] = useState<number>(0);
    const { showToast } = useToast();
    const { invalidateProdutoPrecos } = useProdutoInvalidation();

    // Usar o hook useEmpresas para obter as empresas disponíveis
    const { empresas, isLoading: empresasLoading } = useEmpresas();

    const form = useForm<CriarPrecoEmpresaForm>({
        defaultValues: {
            empresaId: 0,
            precoCompra: 0,
            valor: 0,
            margem: 0,
            descontoMaximo: 0,
            desconto: 0,
            precoAberto: false,
        },
    });

    // Query para buscar preços específicos
    const { data: precos = [], isLoading } = useQuery({
        queryKey: produtoKeys.precosEmpresa(produtoId),
        queryFn: async () => {
            const { data } = await axios.get(`/api/produto/${produtoId}/precos-empresa`);
            return data;
        },
        enabled: !!produtoId,
    });

    // Mutation para criar preço específico
    const createMutation = useMutation({
        mutationFn: (data: CriarPrecoEmpresaForm) => {
            return axios.post(`/api/produto/${produtoId}/preco-empresa`, data);
        },
        onSuccess: () => {
            invalidateProdutoPrecos(produtoId);
            setShowModal(false);
            setGeneralError(null);
            showToast('Preço específico adicionado com sucesso!', "success");
            form.reset();
        },
        onError: (error: any) => {
            setGeneralError(null);
            const responseData = error.response?.data;

            if (responseData?.errors) {
                const formattedErrors = transformErrors(responseData.errors);
                for (const [field, error] of Object.entries(formattedErrors)) {
                    form.setError(field as any, error as any);
                }
            } else if (responseData?.isSuccess === false || responseData?.message) {
                const errorMessage = responseData?.message || 'Erro ao adicionar preço específico';
                setGeneralError(errorMessage);
            } else {
                setGeneralError('Erro inesperado ao adicionar preço específico. Tente novamente.');
            }
        }
    });

    // Mutation para atualizar preço específico
    const updateMutation = useMutation({
        mutationFn: ({ empresaId, ...data }: CriarPrecoEmpresaForm & { empresaId: number }) => {
            return axios.put(`/api/produto/${produtoId}/preco-empresa/${empresaId}`, data);
        },
        onSuccess: () => {
            invalidateProdutoPrecos(produtoId);
            setShowModal(false);
            setEditingPreco(null);
            setGeneralError(null);
            showToast('Preço específico atualizado com sucesso!', "success");
            form.reset();
        },
        onError: (error: any) => {
            setGeneralError(null);
            const responseData = error.response?.data;

            if (responseData?.errors) {
                const formattedErrors = transformErrors(responseData.errors);
                for (const [field, error] of Object.entries(formattedErrors)) {
                    form.setError(field as any, error as any);
                }
            } else if (responseData?.isSuccess === false || responseData?.message) {
                const errorMessage = responseData?.message || 'Erro ao atualizar preço específico';
                setGeneralError(errorMessage);
            } else {
                setGeneralError('Erro inesperado ao atualizar preço específico. Tente novamente.');
            }
        }
    });

    // Mutation para deletar preço específico
    const deleteMutation = useMutation({
        mutationFn: (empresaId: number) => {
            return axios.delete(`/api/produto/${produtoId}/preco-empresa/${empresaId}`);
        },
        onSuccess: () => {
            invalidateProdutoPrecos(produtoId);
            showToast('Preço específico removido com sucesso!', "success");
        },
    });

    const handleOpenModal = (preco?: ProdutoPrecoEmpresa) => {
        if (preco) {
            setEditingPreco(preco);
            form.reset({
                empresaId: preco.empresaId,
                precoCompra: preco.precoCompra,
                valor: preco.valor,
                margem: preco.margem,
                descontoMaximo: preco.descontoMaximo || 0,
                desconto: preco.desconto || 0,
                precoAberto: preco.precoAberto,
            });

            const valor = preco.valor || 0;
            const desconto = preco.desconto || 0;
            const precoInicial = valor - (valor * desconto / 100);
            setPrecoComDesconto(precoInicial);
        } else {
            setEditingPreco(null);
            form.reset();
            setPrecoComDesconto(0);
        }
        setGeneralError(null);
        setShowModal(true);
    };

    const handleSubmit = (data: CriarPrecoEmpresaForm) => {
        setGeneralError(null);
        if (editingPreco) {
            updateMutation.mutate({ ...data, empresaId: editingPreco.empresaId });
        } else {
            createMutation.mutate(data);
        }
    };

    const empresasComPreco = precos.map((p: ProdutoPrecoEmpresa) => p.empresaId);
    const empresasDisponiveis = empresas.filter((e) => !empresasComPreco.includes(e.id));

    return (
        <Accordion className="mb-3">
            <Accordion.Item eventKey="0">
                <Accordion.Header>
                    <div className="d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon={faStore} className="text-primary" />
                        <span>Preços Específicos por Loja</span>
                        {precos.length > 0 && (
                            <Badge bg="primary" className="ms-2">
                                {precos.length}
                            </Badge>
                        )}
                    </div>
                </Accordion.Header>
                <Accordion.Body>
                    <div className="d-flex justify-content-end align-items-center mb-3">
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleOpenModal()}
                            disabled={empresasDisponiveis.length === 0}
                        >
                            <FontAwesomeIcon icon={faPlus} className="me-1" />
                            Adicionar
                        </Button>
                    </div>

                    {isLoading || empresasLoading ? (
                        <div className="text-center py-3">
                            <Spinner animation="border" size="sm" />
                        </div>
                    ) : precos.length === 0 ? (
                        <div className="text-center py-4 text-body-tertiary">
                            <FontAwesomeIcon icon={faStore} size="2x" className="mb-2 opacity-50" />
                            <p>Nenhum preço específico configurado</p>
                            <small>Os preços globais serão aplicados a todas as lojas</small>
                        </div>
                    ) : (
                        <div style={{ border: "1px solid var(--phoenix-border-color)", borderRadius: "0.375rem", overflow: "hidden" }}>
                            <Table responsive hover size="sm" className="align-middle mb-0">
                                <thead className="bg-body-secondary">
                                    <tr className="border-translucent">
                                        <th className="fw-bold text-body-emphasis border-translucent">Loja</th>
                                        <th className="text-end fw-bold text-body-emphasis border-translucent">Preço Compra</th>
                                        <th className="text-end fw-bold text-body-emphasis border-translucent">Margem (%)</th>
                                        <th className="text-end fw-bold text-body-emphasis border-translucent">Preço Venda</th>
                                        <th className="text-end fw-bold text-body-emphasis border-translucent">Preço c/ Desc.</th>
                                        <th className="text-center fw-bold text-body-emphasis border-translucent">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {precos.map((preco: ProdutoPrecoEmpresa) => {
                                        const empresa = empresas.find((e) => e.id === preco.empresaId);
                                        const precoComDesconto = preco.valor - ((preco.desconto ?? 0) * preco.valor) / 100;
                                        return (
                                            <tr key={preco.empresaId} className="border-translucent">
                                                <td className="text-body">{empresa?.nomeFantasia || `Empresa ${preco.empresaId}`}</td>
                                                <td className="text-end text-body">R$ {preco.precoCompra.toFixed(2)}</td>
                                                <td className="text-end text-body">{preco.margem.toFixed(2)}%</td>
                                                <td className="text-end text-body">R$ {preco.valor.toFixed(2)}</td>
                                                <td className="text-end text-body">R$ {precoComDesconto.toFixed(2)}</td>
                                                <td className="text-center align-middle">
                                                    <div className="d-flex gap-1 align-middle justify-content-center">
                                                        <Button
                                                            variant="link"
                                                            size="sm"
                                                            className="text-primary p-0"
                                                            onClick={() => handleOpenModal(preco)}
                                                        >
                                                            <FontAwesomeIcon icon={faEdit} />
                                                        </Button>
                                                        <Button
                                                            variant="link"
                                                            size="sm"
                                                            className="text-danger p-0"
                                                            onClick={() => deleteMutation.mutate(preco.empresaId)}
                                                            disabled={deleteMutation.isPending}
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Accordion.Body>
            </Accordion.Item>

            {/* Modal para adicionar/editar preço específico */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingPreco ? "Editar Preço Específico" : "Adicionar Preço Específico"}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    form.handleSubmit(handleSubmit)();
                }}>
                    <Modal.Body>
                        {generalError && (
                            <Alert variant="danger" dismissible onClose={() => setGeneralError(null)}>
                                <strong>❌ Erro:</strong> {generalError}
                            </Alert>
                        )}

                        <Row className="g-3">
                            {!editingPreco && (
                                <Form.Group as={Col} xs={12} controlId="empresaId">
                                    <Form.Label>Loja *</Form.Label>
                                    <Form.Select
                                        {...form.register("empresaId", {
                                            required: "Selecione uma loja",
                                            valueAsNumber: true,
                                        })}
                                        isInvalid={!!form.formState.errors.empresaId}
                                    >
                                        <option value={0}>Selecione uma loja</option>
                                        {empresasDisponiveis.map((empresa) => (
                                            <option key={empresa.id} value={empresa.id}>
                                                {empresa.nomeFantasia}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {form.formState.errors.empresaId?.message}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            )}

                            <Form.Group as={Col} xs={12} md={4} controlId="precoCompra">
                                <Form.Label>Preço de Compra *</Form.Label>
                                <Controller
                                    name="precoCompra"
                                    control={form.control}
                                    rules={{
                                        required: "Preço de compra é obrigatório",
                                        min: { value: 0.01, message: "Preço de compra deve ser maior que zero" }
                                    }}
                                    render={({ field }) => (
                                        <Money
                                            {...field}
                                            isInvalid={!!form.formState.errors.precoCompra}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                field.onChange(e);
                                                const precoCompra = floatFormat(e.target.value);
                                                const margem = floatFormat(form.watch("margem"));
                                                const valor = precoCompra * (1 + margem / 100);
                                                form.setValue("valor", valor);
                                                const desconto = floatFormat(form.watch("desconto") ?? 0);
                                                const novoPrecoComDesconto = valor - (valor * desconto / 100);
                                                setPrecoComDesconto(novoPrecoComDesconto);
                                            }}
                                        />
                                    )}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {form.formState.errors.precoCompra?.message}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group as={Col} xs={12} md={4} controlId="margem">
                                <Form.Label>Margem (%) *</Form.Label>
                                <Controller
                                    name="margem"
                                    control={form.control}
                                    rules={{
                                        required: "Margem é obrigatória",
                                        min: { value: 0, message: "Margem deve ser maior ou igual a zero" }
                                    }}
                                    render={({ field }) => (
                                        <Money
                                            {...field}
                                            isInvalid={!!form.formState.errors.margem}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                field.onChange(e);
                                                const precoCompra = floatFormat(form.watch("precoCompra"));
                                                const margem = floatFormat(e.target.value);
                                                const valor = precoCompra * (1 + margem / 100);
                                                form.setValue("valor", valor);
                                                const desconto = floatFormat(form.watch("desconto") ?? 0);
                                                const novoPrecoComDesconto = valor - (valor * desconto / 100);
                                                setPrecoComDesconto(novoPrecoComDesconto);
                                            }}
                                        />
                                    )}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {form.formState.errors.margem?.message}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group as={Col} xs={12} md={4} controlId="valor">
                                <Form.Label>Preço de Venda *</Form.Label>
                                <Controller
                                    name="valor"
                                    control={form.control}
                                    rules={{
                                        required: "Preço de venda é obrigatório",
                                        min: { value: 0.01, message: "Preço de venda deve ser maior que zero" }
                                    }}
                                    render={({ field }) => (
                                        <Money
                                            {...field}
                                            isInvalid={!!form.formState.errors.valor}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                field.onChange(e);
                                                const precoCompra = floatFormat(form.watch("precoCompra"));
                                                const valor = floatFormat(e.target.value);
                                                const margem = ((valor - precoCompra) / precoCompra) * 100;
                                                form.setValue("margem", margem);
                                                const desconto = floatFormat(form.watch("desconto") ?? 0);
                                                const novoPrecoComDesconto = valor - (valor * desconto / 100);
                                                setPrecoComDesconto(novoPrecoComDesconto);
                                            }}
                                        />
                                    )}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {form.formState.errors.valor?.message}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group as={Col} xs={12} md={4} controlId="desconto">
                                <Form.Label>Desconto Automático</Form.Label>
                                <Controller
                                    name="desconto"
                                    control={form.control}
                                    render={({ field }) => (
                                        <Money
                                            {...field}
                                            isInvalid={!!form.formState.errors.desconto}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                field.onChange(e);
                                                const desconto = floatFormat(e.target.value);
                                                const valor = floatFormat(form.watch("valor"));
                                                const novoPrecoComDesconto = valor - (valor * desconto / 100);
                                                setPrecoComDesconto(novoPrecoComDesconto);
                                            }}
                                        />
                                    )}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {form.formState.errors.desconto?.message}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group as={Col} xs={12} md={4} controlId="descontoMaximo">
                                <Form.Label>Desconto Máximo</Form.Label>
                                <Controller
                                    name="descontoMaximo"
                                    control={form.control}
                                    render={({ field }) => (
                                        <Money {...field} isInvalid={!!form.formState.errors.descontoMaximo} />
                                    )}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {form.formState.errors.descontoMaximo?.message}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group as={Col} xs={12} md={4} controlId="precoComDesconto">
                                <Form.Label>Preço com Desconto</Form.Label>
                                <Money
                                    value={precoComDesconto}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const novoPrecoComDesconto = floatFormat(e.target.value);
                                        const valor = floatFormat(form.watch("valor"));

                                        if (valor > 0) {
                                            const novoDesconto = ((valor - novoPrecoComDesconto) / valor) * 100;
                                            const descontoFinal = Math.max(0, novoDesconto); // Garante que não seja negativo
                                            form.setValue("desconto", descontoFinal);
                                        }

                                        setPrecoComDesconto(novoPrecoComDesconto);
                                    }}
                                />
                            </Form.Group>

                            <Form.Group as={Col} xs={12} controlId="precoAberto">
                                <Form.Check
                                    type="switch"
                                    id="precoAberto"
                                    label="Preço Aberto"
                                    {...form.register("precoAberto")}
                                    isInvalid={!!form.formState.errors.precoAberto}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {form.formState.errors.precoAberto?.message}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setShowModal(false);
                            }}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            loading={createMutation.isPending || updateMutation.isPending}
                            disabled={createMutation.isPending || updateMutation.isPending}
                        >
                            {editingPreco ? "Atualizar" : "Adicionar"}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Accordion>
    );
};

export default ProdutoEmpresaPrecosEspecificosTable; 